import { useAttachmentViewerState } from "@/zustand/AttachmentViewerState";
import { memo } from "react";
import { Loader, Play } from "lucide-react";
import useMediaAttachment from "@/middleware/hooks/attachments/useMediaAttachment";
import { MediaAttachmentThumbnailProps } from "./utils/attachmentComparison";

/**
 * MediaAttachmentThumbnail component
 *
 * Purpose:
 * Displays a thumbnail preview of media attachments in the media tab.
 *
 * Context:
 * Used within the media tab to show a grid of media attachments.
 * Handles loading states and provides play button overlay for videos.
 * Opens the attachment viewer when clicked.
 *
 * Note:
 * Uses the same useMediaAttachment hook as the main MediaAttachment component
 * but configured to load thumbnails instead of full-sized media.
 * Displays a thumbnail for a media attachment
 * Memoized to prevent unnecessary re-renders
 */
function MediaAttachmentThumbnail({
  messageId,
  chatId,
  timestamp,
  attachment,
  onClick,
}: MediaAttachmentThumbnailProps) {
  const openDialog = useAttachmentViewerState((state) => state.openDialog);

  // Handle click to open attachment viewer
  function handleClick() {
    // Use the MessageAttachment approach for opening the viewer
    // Details drawer is main-chat only (opening a thread closes it), so the
    // viewer scopes to the main chat.
    openDialog(
      {
        id: attachment.attachmentId,
        messageId,
        chatId,
        timestamp,
        filename: attachment.filename,
        fileType: attachment.fileType,
        isLargeAttachment: attachment.isLargeAttachment,
        // Carry the preview meta so a drawer-opened item gets the same
        // first-paint framing + ThumbHash blur as a bubble-opened one.
        width: attachment.width,
        height: attachment.height,
        placeholder: attachment.placeholder,
      },
      {
        // A chunked video opened from the drawer needs these to register its
        // service-worker stream (same as the bubble/carousel open paths).
        uploadState: attachment.uploadState,
        fileSize: attachment.fileSize,
      },
    );

    // Call the onClick handler if provided
    if (onClick) {
      onClick();
    }
  }

  // Use the same hook as the middle column component with stable references
  const { loading, attachmentUrl } = useMediaAttachment({
    messageId,
    chatId,
    attachment,
    messageTimestamp: timestamp,
    getThumbnail: true,
  });

  return (
    <div
      className="relative aspect-square w-full cursor-pointer overflow-hidden rounded-sm"
      onClick={handleClick}
    >
      {loading && (
        <div className="absolute left-0 top-0 z-10 flex size-full animate-pulse items-center justify-center rounded-sm bg-muted align-middle">
          <Loader className="size-5 animate-spin text-secondary" />
        </div>
      )}
      {attachmentUrl && (
        <img
          className="aspect-auto size-full rounded-sm object-cover"
          src={attachmentUrl}
          alt={attachment.filename}
        />
      )}
      {attachment.fileType.startsWith("video/") && attachmentUrl && (
        <div className="absolute left-0 top-0 flex size-full items-center justify-center bg-black/30">
          <div className="flex items-center justify-center rounded-full bg-primary/30 p-1.5">
            <Play className="size-6 text-white" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Custom equality function for MediaAttachmentThumbnail component memoization
 * Prevents unnecessary re-renders by comparing only the essential props
 */
function areAttachmentThumbnailPropsEqual(
  prevProps: MediaAttachmentThumbnailProps,
  nextProps: MediaAttachmentThumbnailProps,
): boolean {
  return (
    prevProps.messageId === nextProps.messageId &&
    prevProps.chatId === nextProps.chatId &&
    prevProps.timestamp === nextProps.timestamp &&
    prevProps.attachment.attachmentId === nextProps.attachment.attachmentId
  );
}

/**
 * Export a memoized version of MediaAttachmentThumbnail to prevent unnecessary re-renders
 * Uses custom equality function to compare props deeply
 */
export default memo(MediaAttachmentThumbnail, areAttachmentThumbnailPropsEqual);
